import React, { useCallback, useEffect } from 'react';
import { Button, Drawer } from 'components/common';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import TinymceEditor from 'components/common/TinymceEditor';
import EmployeeRecipientSelect from 'components/common/select/EmployeeRecipientSelect';
import { getProjectBuildings } from 'redux/crm/project/quotationSlice';
import { createTopic, updateTopic } from 'redux/crm/project/interactionSlice';
import { getAllProjects } from 'redux/crm/projectSlice';
import { tagMeta, TAG_NAMES } from './tagMeta';
import SavedCcListActions from './SavedCcListActions';

const PRIORITY_OPTIONS = [
  { value: 'low',    label: '🟦 Low' },
  { value: 'medium', label: '🟨 Medium' },
  { value: 'high',   label: '🟥 High' },
];

const COMMON_TAGS = TAG_NAMES.map((value) => ({ value, label: value }));

// Dùng chung cho dropdown và chip đã chọn, nên tag tự gõ (creatable) cũng có icon.
const formatTagLabel = ({ label, value }) => {
  const { Icon, color } = tagMeta(value || label);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon size={14} style={{ color, flexShrink: 0 }} />
      {label}
    </span>
  );
};
formatTagLabel.propTypes = { label: PropTypes.string, value: PropTypes.string };

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const FIELD_LABEL = {
  style: {
    fontSize: '0.8rem', fontWeight: 600, color: '#374151',
    marginBottom: 4, display: 'block',
  },
};

const SECTION_TITLE = {
  style: {
    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: '#6b7280',
    padding: '10px 0 6px',
    borderBottom: '1px solid #f1f5f9',
    marginBottom: 12,
  },
};

const TopicForm = ({ show, onHide, loading, record, projectId: propProjectId }) => {
  const dispatch = useDispatch();
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [buildingOptions, setBuildingOptions] = React.useState([]);
  const [isLoadingBuildings, setIsLoadingBuildings] = React.useState(false);

  const { control, register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue, getValues } = useForm({
    defaultValues: {
      name: '', description: '', priority: 'medium',
      tags: [], recipient_names: [], cc_names: [], buildings: [], deal_id: null,
    },
  });

  useEffect(() => {
    if (!show) return;
    if (record) {
      setValue('name', record.name || '');
      setValue('description', record.description || '');
      setValue('priority', record.priority || 'medium');

      if (record.project) {
        const opt = { value: record.project.id, label: record.project.name, sales_incharge: record.project.sales_incharge || '' };
        setValue('deal_id', opt);
        setSelectedProject(opt);
      }

      const tags = Array.isArray(record.tags)
        ? record.tags.map((t) => ({ value: typeof t === 'string' ? t : t.value, label: typeof t === 'string' ? t : t.label }))
        : [];
      setValue('tags', tags);

      // Plain employee codes — EmployeeRecipientSelect's value contract, and what
      // the API stores in recipient_names.
      let recipients = [];
      if (Array.isArray(record.recipient_names)) {
        recipients = record.recipient_names;
      } else if (typeof record.recipient_names === 'string') {
        recipients = record.recipient_names.split(',').map((n) => n.trim()).filter(Boolean);
      }
      setValue('recipient_names', recipients);
      setValue('cc_names', Array.isArray(record.cc_names) ? record.cc_names : []);

      const buildings = Array.isArray(record.buildings)
        ? record.buildings.map((b) => ({ value: b.id, label: b.name }))
        : [];
      setValue('buildings', buildings);
    } else {
      reset({ name: '', description: '', priority: 'medium', tags: [], recipient_names: [], cc_names: [], buildings: [], deal_id: null });
      setSelectedProject(null);
      setBuildingOptions([]);
    }
  }, [show, record, setValue, reset]);

  const applyProject = useCallback((opt) => {
    setValue('deal_id', opt);
    setSelectedProject(opt);
    // Mã nhân viên trần, không phải { value, label }: EmployeeRecipientSelect
    // nhận string[] và dùng chính giá trị làm key cache, nên một object sẽ ra
    // chip "[object Object]" và gửi rác lên recipient_names.
    if (opt?.sales_incharge) setValue('recipient_names', [opt.sales_incharge]);
    setValue('buildings', []);
  }, [setValue]);

  const fetchBuildings = useCallback(async (pid) => {
    if (!pid) { setBuildingOptions([]); return; }
    setIsLoadingBuildings(true);
    try {
      const res = await dispatch(getProjectBuildings({ projectId: pid })).unwrap();
      const options = res.rows?.map((r) => r.attributes).map((b) => ({ value: b.id, label: b.name })) || [];
      setBuildingOptions(options);
      // Chọn sẵn mọi building của project; người dùng bỏ bớt cái không liên
      // quan. Topic đang sửa đã có building thì giữ nguyên.
      if (options.length && !(getValues('buildings') || []).length) {
        setValue('buildings', options, { shouldValidate: true });
      }
    } catch {
      setBuildingOptions([]);
    } finally {
      setIsLoadingBuildings(false);
    }
  }, [dispatch, getValues, setValue]);

  const loadProjects = useCallback(async (inputValue) => {
    try {
      const params = { page: 1, per_page: 30 };
      if (propProjectId) params.id_cont = propProjectId;
      if ((inputValue || '').trim().length > 2) params.name_or_q_number_or_j_number_cont = inputValue.trim();
      const res = await dispatch(getAllProjects(params)).unwrap();
      return (Array.isArray(res?.rows) ? res.rows.map((r) => r.attributes) : []).map((item) => ({
        value: item.id,
        label: `${item.name} — ${item.j_number || item.q_number || ''}`,
        sales_incharge: item.sales_incharge || '',
      }));
    } catch {
      return [];
    }
  }, [dispatch, propProjectId]);

  // Mở từ trang chi tiết project (có projectId): chọn sẵn project y như người
  // dùng tự chọn — sales in-charge vào To, building được load ngay.
  useEffect(() => {
    if (!show || record || !propProjectId) return undefined;
    let cancelled = false;
    loadProjects('').then((opts) => {
      const opt = opts.find((o) => String(o.value) === String(propProjectId));
      if (!cancelled && opt) applyProject(opt);
    });
    return () => { cancelled = true; };
  }, [show, record, propProjectId, loadProjects, applyProject]);

  useEffect(() => {
    if (selectedProject?.value) fetchBuildings(selectedProject.value);
  }, [selectedProject, fetchBuildings]);

  const handleSubmitTopic = async (data) => {
    if (!selectedProject?.value) return;
    const payload = {
      ...data,
      deal_id: undefined,
      buildings: Array.isArray(data.buildings) ? data.buildings.map((b) => ({ id: b.value, name: b.label })) : [],
      tags: Array.isArray(data.tags) ? data.tags.map((t) => t.value) : [],
      // Already plain employee codes — EmployeeRecipientSelect emits them directly.
      recipient_names: Array.isArray(data.recipient_names) ? data.recipient_names : [],
      cc_names: Array.isArray(data.cc_names) ? data.cc_names : [],
    };
    try {
      const pid = selectedProject.value;
      if (record) {
        await dispatch(updateTopic({ projectId: pid, topicId: record.id, data: payload })).unwrap();
      } else {
        await dispatch(createTopic({ projectId: pid, data: payload })).unwrap();
      }
      onHide();
    } catch {
      // Error handled in Redux
    }
  };

  const busy = loading || isSubmitting;

  const fieldError = (error) => error && (
    <div className="tw-text-red-600 tw-text-xs tw-mt-1">{error.message}</div>
  );

  return (
    <Drawer
      show={show}
      onHide={onHide}
      title={record ? 'Edit Topic' : 'New Topic'}
      placement="right"
      width="min(62%, 900px)"
      minWidth="480px"
      backdropClick={false}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide} disabled={busy} size="sm" style={{ minWidth: 80 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="interaction-topic-form"
            disabled={busy}
            loading={busy}
            size="sm"
            style={{
              minWidth: 120, background: 'linear-gradient(135deg, #1e3a5f, #1d4ed8)',
              border: 'none', fontWeight: 600,
            }}
          >
            {busy ? (record ? 'Updating...' : 'Creating...') : (record ? 'Update Topic' : 'Create Topic')}
          </Button>
        </>
      )}
    >
      <div className="-tw-mx-6 -tw-my-5">
        <form id="interaction-topic-form" onSubmit={handleSubmit(handleSubmitTopic)}>
          <div style={{ padding: '20px 24px', background: '#f8fafc' }}>

            {/* ── Basic Info ─────────────────────────────── */}
            <div {...SECTION_TITLE}>Basic Information</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 14, marginBottom: 14 }}>
              {/* Title */}
              <div>
                <label {...FIELD_LABEL}>Title <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Enter topic title..."
                  className={INPUT_CLASSES}
                  {...register('name', { required: 'Title is required', minLength: { value: 3, message: 'Min 3 characters' } })}
                  style={{ fontSize: '0.875rem' }}
                />
                {fieldError(errors.name)}
              </div>

              {/* Priority */}
              <div>
                <label {...FIELD_LABEL}>Priority</label>
                <select {...register('priority')} className={INPUT_CLASSES} style={{ fontSize: '0.875rem' }}>
                  {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: 14 }}>
              <label {...FIELD_LABEL}>Description <span style={{ color: '#dc2626' }}>*</span></label>
              <Controller
                name="description"
                control={control}
                rules={{ required: 'Description is required' }}
                render={({ field }) => (
                  <>
                    <TinymceEditor
                      handleChange={field.onChange}
                      value={field.value || ''}
                      placeholder="Describe your topic..."
                      height={280}
                      resizable
                      minHeight={200}
                      maxHeight={720}
                    />
                    {fieldError(errors.description)}
                  </>
                )}
              />
            </div>

            {/* ── Project & Buildings ────────────────────── */}
            <div {...SECTION_TITLE}>Project & Buildings</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              {/* Project */}
              <div>
                <label {...FIELD_LABEL}>Project <span style={{ color: '#dc2626' }}>*</span></label>
                <Controller
                  control={control}
                  name="deal_id"
                  rules={{ required: 'Project is required' }}
                  render={({ field }) => (
                    <AsyncSelect
                      {...field}
                      cacheOptions
                      defaultOptions
                      loadOptions={loadProjects}
                      placeholder="Search project..."
                      classNamePrefix="react-select"
                      isClearable
                      noOptionsMessage={({ inputValue }) => inputValue ? `No results for "${inputValue}"` : 'Type to search'}
                      onChange={(opt) => { field.onChange(opt); applyProject(opt); }}
                      {...portalProps}
                    />
                  )}
                />
                {fieldError(errors.deal_id)}
              </div>

              {/* Buildings */}
              <div>
                <label {...FIELD_LABEL}>Buildings <span style={{ color: '#dc2626' }}>*</span></label>
                <Controller
                  control={control}
                  name="buildings"
                  rules={{ required: 'Select at least one building' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={buildingOptions}
                      isMulti
                      closeMenuOnSelect={false}
                      isDisabled={!selectedProject || isLoadingBuildings}
                      isLoading={isLoadingBuildings}
                      placeholder={!selectedProject ? 'Select project first...' : 'Select buildings...'}
                      classNamePrefix="react-select"
                      isClearable
                      onChange={(opts) => field.onChange(opts || [])}
                      {...portalProps}
                    />
                  )}
                />
                {fieldError(errors.buildings)}
              </div>
            </div>

            {/* ── People & Tags ──────────────────────────── */}
            <div {...SECTION_TITLE}>Recipients & Tags</div>

            {/* Stacked, not side by side: both are multi-selects whose menu opens
                downward and grows with the result list, so a two-column row meant
                the Recipients menu covered the Tags field next to it. Full width
                also gives the chips room instead of wrapping after two. */}
            <div style={{ display: 'grid', gap: 14 }}>
              {/* Recipients — To */}
              <div>
                <label {...FIELD_LABEL}>
                  Recipients (To) <span style={{ color: '#dc2626' }}>*</span>
                  <span style={{ color: '#94a3b8', fontWeight: 400 }}> — người được hỏi, sẽ nhận và trả lời</span>
                </label>
                <Controller
                  control={control}
                  name="recipient_names"
                  rules={{ validate: (v) => (v?.length > 0) || 'Select at least one recipient' }}
                  render={({ field }) => (
                    <EmployeeRecipientSelect
                      value={field.value}
                      onChange={field.onChange}
                      hasError={!!errors.recipient_names}
                      placeholder="Search recipients..."
                    />
                  )}
                />
                {fieldError(errors.recipient_names)}
              </div>

              {/* CC — theo dõi, không phải người được hỏi. Đi kèm cả mail tạo
                  topic lẫn mọi mail trả lời trong topic đó. */}
              <div>
                <label {...FIELD_LABEL}>
                  CC <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional) — các bên liên quan chỉ theo dõi</span>
                </label>
                <Controller
                  control={control}
                  name="cc_names"
                  render={({ field }) => (
                    <>
                      <EmployeeRecipientSelect
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Search people to CC..."
                      />
                      <SavedCcListActions value={field.value} onApply={field.onChange} enabled={show} />
                    </>
                  )}
                />
              </div>

              {/* Tags */}
              <div>
                <label {...FIELD_LABEL}>Tags <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      {...field}
                      isMulti
                      closeMenuOnSelect={false}
                      options={COMMON_TAGS}
                      placeholder="Select or type tags..."
                      classNamePrefix="react-select"
                      isClearable
                      formatOptionLabel={formatTagLabel}
                      formatCreateLabel={(v) => `Create "${v}"`}
                      noOptionsMessage={() => 'Type to create a tag'}
                      {...portalProps}
                    />
                  )}
                />
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {COMMON_TAGS.slice(0, 5).map(({ value }) => {
                    const { Icon, color } = tagMeta(value);
                    return (
                      <span key={value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Icon size={12} style={{ color }} />{value}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
    </Drawer>
  );
};

// The menu must be portalled out: the drawer body scrolls (overflow-y-auto), so
// an inline menu is clipped by it and the options overlap the fields below.
// z-index clears the drawer panel (1200).
const selectSmallStyles = {
  control:    (base) => ({ ...base, fontSize: '0.875rem', minHeight: 34 }),
  option:     (base) => ({ ...base, fontSize: '0.875rem' }),
  menuPortal: (base) => ({ ...base, zIndex: 1300 }),
};

const portalProps = {
  styles: selectSmallStyles,
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  // Fields near the bottom of the drawer open their menu upward instead of
  // running under the footer, and the list scrolls rather than growing forever.
  menuPlacement: 'auto',
  maxMenuHeight: 260,
};

TopicForm.propTypes = {
  show:      PropTypes.bool.isRequired,
  onHide:    PropTypes.func.isRequired,
  loading:   PropTypes.bool,
  record:    PropTypes.object,
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TopicForm;
